import { Visibility, VisibilityOff } from "@mui/icons-material";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import { motion } from "framer-motion";
import sha256 from "js-sha256";
import JSEncrypt from "jsencrypt";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ERP_BASE_URL } from "../../../config/apiConfig";

const logoUrl = "/assets/zypp-logo.png";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetSuccess, setResetSuccess] = useState<string | null>(null);
  const [resetLoader, setResetLoader] = useState(false);
  const [tokenValid, setTokenValid] = useState(true);

  // Extract token from URL on component mount
  useEffect(() => {
    const token = searchParams.get("token");
    if (token) {
      setResetToken(token);
    } else {
      setTokenValid(false);
      setResetError("Invalid reset link. No token found.");
    }
  }, [searchParams]);

  const hexToBytes = (hex: string) => {
    const bytes: number[] = [];
    for (let i = 0; i < hex.length; i += 2) {
      bytes.push(parseInt(hex.substr(i, 2), 16));
    }
    return bytes;
  };

  const handleResetPassword = async () => {
    if (newPassword !== confirmPassword) {
      setResetError("Passwords do not match!");
      return;
    }

    if (newPassword.length < 6) {
      setResetError("Password must be at least 6 characters long!");
      return;
    }

    setResetLoader(true);
    setResetError(null);
    setResetSuccess(null);

    try {
      // Encrypt the new password like in login
      const timestamp = String(Date.now());
      const hash = sha256.sha256(newPassword + timestamp);
      const hashBytes = hexToBytes(hash);
      const hashBase64 = btoa(String.fromCharCode(...hashBytes));
      const data = `${newPassword}:${timestamp}:${hashBase64}`;
      const encrypt = new JSEncrypt();
      encrypt.setPublicKey(
        "MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAg79k2m8PlGNIyJum2uzGqnhdjNqc90SF5FwoOlU7I6Hy6xh36K2qzw1Q5wLcfM+KojFy0xLzKf/u03Wm9S/SmbwvzlXaCq5uYEtVqJBxCz3Y+ZyTRlWyWDJki+OvGYqGbw1EmmKFt2SaOjsE/e+lo6KQpFZwGuaJ1ZKQjCbKRdmkOUAWIjK51a3r8kTnq7XIMf9iXaHX5qvTS/BMcRrXTQ5/JGeuy9OCaJ0S7YrSINZymd0puA0IH87QxDmnUoq+pJwo8l67MFwK7rLyCDSRGwdVY4kLQw8T25k53eoH6+ZUMLydkR4X/ryXYERIw/OwPWpoioHARKzyA/ARA2jkTQIDAQAB"
      );

      const encryptedPassword = encrypt.encrypt(data);
      if (!encryptedPassword) throw new Error("Encryption failed. Invalid data.");

      const response = await axios.put(
        `${ERP_BASE_URL}/erp/user/change/password`,
        {
          newPassword: encryptedPassword,
          confirmPassword: encryptedPassword,
        },
        {
          params: { token: resetToken },
        }
      );

      setResetLoader(false);
      setResetSuccess("Password reset successfully! Redirecting to login...");
      setTimeout(() => {
        navigate("/");
      }, 2000);
    } catch (error: any) {
      console.error("Reset password error:", error);
      setResetLoader(false);
      const errorMessage =
        error.response?.data?.message ||
        "Failed to reset password. Please try again.";
      setResetError(errorMessage);
      // If token is invalid, show link to request new reset
      if (
        error.response?.status === 401 ||
        error.response?.status === 400
      ) {
        setResetError(
          `${errorMessage} Please request a new reset link.`
        );
        setTokenValid(false);
      }
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Animated background elements */}
      <Box
        sx={{
          position: "absolute",
          width: "100%",
          height: "100%",
          overflow: "hidden",
        }}
      >
        <motion.div
          style={{
            position: "absolute",
            width: "600px",
            height: "600px",
            borderRadius: "40% 60% 70% 30% / 40% 50% 60% 50%",
            background:
              "linear-gradient(135deg, rgba(0, 0, 0, 0.02) 0%, rgba(0, 0, 0, 0.05) 100%)",
            top: "-300px",
            left: "-200px",
          }}
          animate={{
            rotate: [0, 360],
          }}
          transition={{
            duration: 60,
            repeat: Infinity,
            ease: "linear",
          }}
        />
        <motion.div
          style={{
            position: "absolute",
            width: "400px",
            height: "400px",
            borderRadius: "30% 70% 70% 30% / 30% 30% 70% 70%",
            background:
              "linear-gradient(135deg, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0.2) 100%)",
            bottom: "-200px",
            right: "-200px",
          }}
          animate={{
            rotate: [0, -360],
          }}
          transition={{
            duration: 45,
            repeat: Infinity,
            ease: "linear",
          }}
        />
      </Box>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <Paper
          elevation={0}
          sx={{
            p: { xs: 4, sm: 5, md: 6 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            background: "rgba(255, 255, 255, 0.9)",
            backdropFilter: "blur(20px)",
            borderRadius: "24px",
            position: "relative",
            overflow: "hidden",
            maxWidth: "480px",
            width: "100%",
            mx: 2,
            border: "1px solid rgba(0, 0, 0, 0.08)",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.08)",
          }}
        >
          {/* Subtle top accent */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: "50%",
              transform: "translateX(-50%)",
              width: "80px",
              height: "3px",
              background:
                "linear-gradient(90deg, transparent, #000, transparent)",
              opacity: 0.2,
            }}
          />

          {/* Logo */}
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5, ease: "easeOut" }}
          >
            <Box
              component="img"
              src={logoUrl}
              alt="Logo"
              sx={{
                height: 48,
                mb: 4,
                opacity: 0.8,
              }}
            />
          </motion.div>

          {/* Welcome Text */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            <Typography
              variant="h4"
              gutterBottom
              sx={{
                fontWeight: 300,
                color: "#1a1a1a",
                mb: 1,
                letterSpacing: "-0.5px",
                textAlign: "center",
              }}
            >
              Reset Your Password
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "#6b7280",
                mb: 5,
                textAlign: "center",
                fontWeight: 400,
              }}
            >
              Create a new password for your account
            </Typography>
          </motion.div>

          {tokenValid ? (
            <Box sx={{ width: "100%", mt: 1 }}>
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4, duration: 0.5 }}
              >
                <TextField
                  fullWidth
                  label="New Password"
                  type={showNewPassword ? "text" : "password"}
                  variant="outlined"
                  size="medium"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  sx={{
                    mb: 3,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      backgroundColor: "#f9fafb",
                      transition: "all 0.2s",
                      "& fieldset": {
                        borderColor: "rgba(0, 0, 0, 0.08)",
                      },
                      "&:hover": {
                        backgroundColor: "#f3f4f6",
                        "& fieldset": {
                          borderColor: "rgba(0, 0, 0, 0.12)",
                        },
                      },
                      "&.Mui-focused": {
                        backgroundColor: "#fff",
                        "& fieldset": {
                          borderColor: "#374151",
                          borderWidth: "1px",
                        },
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "#6b7280",
                      "&.Mui-focused": {
                        color: "#374151",
                      },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOpenIcon sx={{ color: "#9ca3af", fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          edge="end"
                          sx={{
                            color: "#9ca3af",
                            "&:hover": {
                              backgroundColor: "transparent",
                              color: "#6b7280",
                            },
                          }}
                        >
                          {showNewPassword ? (
                            <Visibility sx={{ fontSize: 20 }} />
                          ) : (
                            <VisibilityOff sx={{ fontSize: 20 }} />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5, duration: 0.5 }}
              >
                <TextField
                  fullWidth
                  label="Confirm Password"
                  type={showConfirmPassword ? "text" : "password"}
                  variant="outlined"
                  size="medium"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  sx={{
                    mb: 4,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      backgroundColor: "#f9fafb",
                      transition: "all 0.2s",
                      "& fieldset": {
                        borderColor: "rgba(0, 0, 0, 0.08)",
                      },
                      "&:hover": {
                        backgroundColor: "#f3f4f6",
                        "& fieldset": {
                          borderColor: "rgba(0, 0, 0, 0.12)",
                        },
                      },
                      "&.Mui-focused": {
                        backgroundColor: "#fff",
                        "& fieldset": {
                          borderColor: "#374151",
                          borderWidth: "1px",
                        },
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "#6b7280",
                      "&.Mui-focused": {
                        color: "#374151",
                      },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOpenIcon sx={{ color: "#9ca3af", fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                          sx={{
                            color: "#9ca3af",
                            "&:hover": {
                              backgroundColor: "transparent",
                              color: "#6b7280",
                            },
                          }}
                        >
                          {showConfirmPassword ? (
                            <Visibility sx={{ fontSize: 20 }} />
                          ) : (
                            <VisibilityOff sx={{ fontSize: 20 }} />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </motion.div>

              {resetError && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Alert
                    severity="error"
                    sx={{
                      mb: 3,
                      borderRadius: "12px",
                      backgroundColor: "#fef2f2",
                      color: "#991b1b",
                      "& .MuiAlert-icon": {
                        color: "#dc2626",
                      },
                      border: "1px solid #fecaca",
                    }}
                  >
                    {resetError}
                  </Alert>
                </motion.div>
              )}

              {resetSuccess && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Alert
                    severity="success"
                    sx={{
                      mb: 3,
                      borderRadius: "12px",
                      backgroundColor: "#f0fdf4",
                      color: "#166534",
                      "& .MuiAlert-icon": {
                        color: "#16a34a",
                      },
                      border: "1px solid #bbf7d0",
                    }}
                  >
                    {resetSuccess}
                  </Alert>
                </motion.div>
              )}

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.5 }}
              >
                <Button
                  onClick={handleResetPassword}
                  fullWidth
                  variant="contained"
                  disabled={
                    resetLoader ||
                    !newPassword ||
                    !confirmPassword ||
                    !tokenValid
                  }
                  sx={{
                    mt: 1,
                    mb: 3,
                    py: 1.75,
                    borderRadius: "12px",
                    textTransform: "none",
                    fontSize: "15px",
                    fontWeight: 500,
                    letterSpacing: "0.3px",
                    background: "#1a1a1a",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                    transition: "all 0.2s",
                    "&:hover": {
                      background: "#000",
                      transform: "translateY(-1px)",
                      boxShadow: "0 6px 20px rgba(0, 0, 0, 0.2)",
                    },
                    "&:active": {
                      transform: "translateY(0)",
                    },
                    "&:disabled": {
                      background: "#e5e7eb",
                      color: "#9ca3af",
                    },
                  }}
                >
                  {resetLoader ? (
                    <CircularProgress size={22} sx={{ color: "white" }} />
                  ) : (
                    "Reset Password"
                  )}
                </Button>
              </motion.div>

              <Box sx={{ textAlign: "center" }}>
                <Button
                  onClick={() => navigate("/")}
                  sx={{
                    textTransform: "none",
                    color: "#6b7280",
                    fontSize: "14px",
                    fontWeight: 500,
                    padding: 0,
                    minWidth: "auto",
                    "&:hover": {
                      backgroundColor: "transparent",
                      color: "#374151",
                      textDecoration: "underline",
                    },
                  }}
                >
                  Back to Login
                </Button>
              </Box>
            </Box>
          ) : (
            <Box sx={{ width: "100%", mt: 1, textAlign: "center" }}>
              {resetError && (
                <Alert
                  severity="error"
                  sx={{
                    mb: 3,
                    borderRadius: "12px",
                    backgroundColor: "#fef2f2",
                    color: "#991b1b",
                    "& .MuiAlert-icon": {
                      color: "#dc2626",
                    },
                    border: "1px solid #fecaca",
                  }}
                >
                  {resetError}
                </Alert>
              )}
              <Typography
                variant="body2"
                sx={{
                  color: "#6b7280",
                  mb: 3,
                  fontWeight: 400,
                }}
              >
                Please request a new password reset link from the login page.
              </Typography>
              <Button
                onClick={() => navigate("/")}
                variant="contained"
                sx={{
                  textTransform: "none",
                  borderRadius: "12px",
                  background: "#1a1a1a",
                  px: 4,
                  py: 1.75,
                  fontSize: "15px",
                  fontWeight: 500,
                  "& :hover": {
                    background: "#000",
                  },
                }}
              >
                Go to Login
              </Button>
            </Box>
          )}
        </Paper>
      </motion.div>
    </Box>
  );
};

export default ResetPassword;
